/**
 * Logistics P&L API service.
 *
 * Types from the OpenAPI-generated client (src/api/generated/logistics.ts) where the
 * contract defines schemas; the cost endpoints only ship examples (no inline schema), so
 * their response shapes are declared here as thin local interfaces matching those examples.
 *
 * City-scoped (Bearer JWT): users only see their own city; cross-city access is 403.
 * Money reads back as decimal STRINGS ("45000.00"); rate/booking bodies send NUMBERS.
 * Setting a vehicle rate or accruing updates the EBITDA Logistics line (costing_type 10).
 */
import { apiService } from './api';
import type { components } from '../api/generated/logistics';

export type SetRateBody = components['schemas']['SetRateBody'];
export type VehicleRate = components['schemas']['VehicleRate'];
export type CreateBookingBody = components['schemas']['CreateBookingBody'];
export type Booking = components['schemas']['Booking'];

/** cost/daily row (contract example only). Money fields are decimal strings. */
export interface DailyCostRow {
	cost_date: string;
	fixed_cost: string;
	adhoc_cost: string;
	total_cost: string;
}
/** cost/weekly + accrue week buckets (numbers). */
export interface WeeklyCost {
	week1: number;
	week2: number;
	week3: number;
	week4: number;
}
export interface AccrueResult extends WeeklyCost {
	facility_id: number;
	month: string;
	seam?: { written?: boolean; total?: number; rpac_id?: number };
}

export class LogisticsApiService {
	/** POST /logistics/vehicles/{id}/rate — set an effective-dated contracted rate. */
	static async setRate(vehicleId: number, body: SetRateBody): Promise<{ id?: number }> {
		const res = await apiService.post<{ data: { id?: number } }>(
			`/logistics/vehicles/${vehicleId}/rate`,
			body
		);
		return (res as unknown as { data?: { id?: number } }).data ?? {};
	}

	/** GET /logistics/vehicles/{id}/rate?asOf= — active rate as-of a date (or null). */
	static async getRate(vehicleId: number, asOf: string): Promise<VehicleRate | null> {
		const res = await apiService.get<{ data: VehicleRate | null }>(
			`/logistics/vehicles/${vehicleId}/rate?asOf=${encodeURIComponent(asOf)}`
		);
		return (res as unknown as { data: VehicleRate | null }).data ?? null;
	}

	/** POST /logistics/bookings — create an ad-hoc (Porter/Rapido) booking; cost hits the beneficiary facility. */
	static async createBooking(body: CreateBookingBody): Promise<Booking> {
		const res = await apiService.post<{ data: Booking }>('/logistics/bookings', body);
		return (res as unknown as { data: Booking }).data;
	}

	/** GET /logistics/bookings?facilityId=&from=&to= — list a facility's bookings. */
	static async listBookings(facilityId: number, from: string, to: string): Promise<Booking[]> {
		const res = await apiService.get<{ data: Booking[] }>(
			`/logistics/bookings?facilityId=${facilityId}&from=${from}&to=${to}`
		);
		return (res as unknown as { data?: Booking[] }).data ?? [];
	}

	/** GET /logistics/cost/daily?facilityId=&date= — fixed + adhoc cost for a day. */
	static async costDaily(facilityId: number, date: string): Promise<DailyCostRow[]> {
		const res = await apiService.get<{ data: DailyCostRow[] }>(
			`/logistics/cost/daily?facilityId=${facilityId}&date=${date}`
		);
		return (res as unknown as { data?: DailyCostRow[] }).data ?? [];
	}

	/** GET /logistics/cost/weekly?facilityId=&month= — week1..4 buckets for a month. */
	static async costWeekly(facilityId: number, month: string): Promise<WeeklyCost | null> {
		const res = await apiService.get<{ data: WeeklyCost }>(
			`/logistics/cost/weekly?facilityId=${facilityId}&month=${month}`
		);
		return (res as unknown as { data: WeeklyCost | null }).data ?? null;
	}

	/** POST /logistics/accrue — accrue a facility's month into the EBITDA Logistics line. */
	static async accrue(facilityId: number, month: string): Promise<AccrueResult> {
		const res = await apiService.post<{ data: AccrueResult }>('/logistics/accrue', {
			facilityId,
			month,
		});
		return (res as unknown as { data: AccrueResult }).data;
	}
}
